import type { IconType } from 'react-icons';
import { CgSpinner } from 'react-icons/cg';
import { HiOutlineNewspaper } from 'react-icons/hi2';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import styles from '../WatchlistInsightsSkeleton.module.css';

dayjs.extend(relativeTime);

export const shortenTimeDateAgo = (date: string | undefined) => {
  if (!date) return '';
  return dayjs(date)
    .fromNow()
    .replace(' hours', 'h')
    .replace(' days', 'd')
    .replace(' minutes', 'm')
    .replace(' seconds', 'secs')
    .replace(' months', 'M')
    .replace(' years', 'Y');
};

const compactHeadlinePlaceholders = [
  'Bond Markets Are Rethinking Big Tech spending plans ahead',
  'Why SoftBank Is Betting Billions On This Chipmaker Now',
  'Director At NVIDIA Sells A Large Block Of Company Stock',
  'Leaving Blue Origin for SpaceX? Reported equity clause',
  'Meta Platforms Sees Fresh Analyst Coverage This Week',
];

const defaultHeadlinePlaceholders = [
  'Market update for your watchlist tickers today',
  'Analyst notes and ratings across selected symbols',
  'Company filing and insider activity highlights',
  'Trading idea and catalyst coverage for the session',
  'Sector movers tied to your current watchlist',
  'Additional headlines while news is still loading',
];

export const NewsListSkeleton = ({
  compact = false,
  count = 8,
  showTimeAgo = false,
}: {
  compact?: boolean;
  count?: number;
  showTimeAgo?: boolean;
}) => {
  const placeholders = compact ? compactHeadlinePlaceholders : defaultHeadlinePlaceholders;

  return (
    <div className="flex flex-col" aria-busy="true" aria-live="polite">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className={
            compact
              ? 'flex items-start gap-3 rounded-lg px-2.5 py-2.5'
              : 'flex items-start gap-3 border-b border-slate-100 py-3.5 first:pt-0 last:border-b-0 last:pb-0'
          }
        >
          <div className="shrink-0 pt-0.5">
            <span
              className={`${styles.boneText} inline-flex min-w-[2.75rem] justify-center rounded-md px-2 py-0.5 text-xs font-bold tabular-nums`}
            >
              AAPL
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p
              className={`${styles.boneText} text-pretty font-semibold leading-snug ${
                compact ? 'text-sm' : 'text-sm sm:text-[15px]'
              }`}
            >
              {placeholders[index % placeholders.length]}
            </p>
            {showTimeAgo ? (
              <span className={`${styles.boneText} mt-1 inline-block text-[11px] font-medium tabular-nums`}>
                6h ago
              </span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};

export const LoadingState = () => {
  return (
    <div className="absolute inset-0 flex min-h-24 items-center justify-center rounded-lg bg-white/80">
      <CgSpinner className="animate-spin text-bzblue-700" size={22} />
    </div>
  );
};

export type EmptyStateAction = {
  label: string;
  onClick: () => void;
};

export type EmptyStateProps = {
  action?: EmptyStateAction;
  compact?: boolean;
  description?: string;
  icon?: IconType;
  message?: string;
  minHeightClass?: string;
  title?: string;
};

export const EmptyList = ({
  action,
  compact = false,
  description,
  icon: Icon = HiOutlineNewspaper,
  message,
  minHeightClass,
  title,
}: EmptyStateProps) => {
  const resolvedTitle = title ?? message ?? 'No headlines for this list right now.';

  return (
    <div
      className={`flex flex-1 flex-col items-center justify-center px-4 text-center ${
        minHeightClass ?? (compact ? 'min-h-[200px] py-8' : 'min-h-[240px] py-10')
      }`}
    >
      <div
        className={`flex flex-col items-center rounded-xl border border-dashed border-slate-200 bg-slate-50 ${
          compact ? 'max-w-sm px-5 py-7' : 'max-w-md px-6 py-8'
        }`}
      >
        <span
          className={`mb-3 flex items-center justify-center rounded-full bg-white text-slate-400 shadow-sm ${
            compact ? 'size-10' : 'size-11'
          }`}
        >
          <Icon size={compact ? 18 : 20} />
        </span>
        <p className={`font-bold text-slate-950 ${compact ? 'text-sm' : 'text-base'}`}>{resolvedTitle}</p>
        {description ? (
          <p className={`mt-1.5 max-w-xs text-balance leading-6 text-slate-500 ${compact ? 'text-xs' : 'text-sm'}`}>
            {description}
          </p>
        ) : null}
        {action ? (
          <button
            type="button"
            className={`mt-4 inline-flex min-h-9 items-center justify-center rounded-md bg-bzblue-700 px-3.5 font-bold text-white transition-colors duration-150 ease-out hover:bg-bzblue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bzblue-700 ${
              compact ? 'text-xs' : 'text-sm'
            }`}
            onClick={action.onClick}
          >
            {action.label}
          </button>
        ) : null}
      </div>
    </div>
  );
};
